/**
 * Airbnb-style card selector for choosing spaces during onboarding.
 * Users can tap to select/deselect spaces and add custom ones.
 *
 * When content exceeds 2 rows, switches to horizontal scrolling mode
 * to prevent vertical overflow in the composer.
 */

import {
  faCheck,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DynamicSpaceSuggestion,
  getDynamicSpaceSuggestions,
  getSpaceSuggestionsForPlaceType,
  inferSpaceIcon,
  parseSpaceName,
  SpaceSuggestion,
} from '~/constants/onboardingPresets';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { ExpandingCustomSpaceInput } from './shared/ExpandingCustomSpaceInput';

// =============================================================================
// SPACE CARD COMPONENT
// =============================================================================

type SpaceCardProps = {
  space: SpaceSuggestion;
  selected: boolean;
  onToggle: () => void;
};

function SpaceCard({ space, selected, onToggle }: SpaceCardProps) {
  return (
    <button
      type='button'
      onClick={onToggle}
      className={classNames(
        'group relative flex flex-col items-center justify-center rounded-xl border-2 p-3 transition-all duration-200',
        'min-h-[100px] w-full',
        selected
          ? 'bg-brand/5 border-brand shadow-md'
          : 'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm'
      )}
    >
      {/* Check indicator */}
      {selected && (
        <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white'>
          <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
        </div>
      )}

      {/* Icon */}
      <div
        className={classNames(
          'mb-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors',
          selected ? 'text-brand' : 'text-grey-50 group-hover:text-grey-70'
        )}
      >
        <FontAwesomeIcon icon={space.icon} className='h-5 w-5' />
      </div>

      {/* Label */}
      <span
        className={classNames(
          'text-center text-sm font-medium',
          selected ? 'text-brand' : 'text-grey-70'
        )}
      >
        {space.name}
      </span>
    </button>
  );
}

// =============================================================================
// CUSTOM SPACE CHIP (FOR DISPLAYING USER-ADDED SPACES)
// =============================================================================

type CustomSpaceChipProps = {
  name: string;
  /** Display name shown on card (defaults to name). Use for sub-spaces to show just child name. */
  displayName?: string;
  icon: Parameters<typeof FontAwesomeIcon>[0]['icon'];
  selected: boolean;
  onToggle: () => void;
  onRemove: () => void;
};

/**
 * Card for user-added custom spaces.
 * Uses same styling as DynamicSpaceCard for consistency.
 */
function CustomSpaceChip({
  name,
  displayName,
  icon,
  selected,
  onToggle,
  onRemove,
}: CustomSpaceChipProps) {
  // Use displayName if provided, otherwise use name
  const label = displayName ?? name;
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };
  return (
    <div
      role='button'
      tabIndex={0}
      aria-pressed={selected}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className={classNames(
        'group relative flex min-h-[100px] w-full flex-col items-center justify-center rounded-xl border-2 p-3 transition-all duration-200',
        selected
          ? 'border-brand bg-white shadow-md'
          : 'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm'
      )}
    >
      {/* Remove button */}
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className='hover:text-grey-60 absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full text-grey-40 opacity-0 transition-opacity hover:bg-grey-10 group-hover:opacity-100'
        aria-label={`Remove ${name}`}
      >
        ×
      </button>

      {/* Check indicator */}
      {selected && (
        <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white'>
          <FontAwesomeIcon icon={faCheck} className='h-3 w-3' />
        </div>
      )}

      {/* Icon - matches DynamicSpaceCard styling */}
      <div
        className={classNames(
          'mb-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors',
          selected ? 'text-brand' : 'text-grey-50 group-hover:text-grey-70'
        )}
      >
        <FontAwesomeIcon icon={icon} className='h-5 w-5' />
      </div>

      {/* Label */}
      <span
        className={classNames(
          'text-center text-sm font-medium',
          selected ? 'text-brand' : 'text-grey-70'
        )}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Get display name for a space (extracts child name from sub-spaces)
 * e.g., "Bedroom 1/Ensuite" → "Ensuite", "Bedroom 3" → "Bedroom 3"
 */
const getDisplayName = (name: string): string => {
  if (name.includes('/')) {
    return name.split('/').pop() ?? name;
  }
  return name;
};

// =============================================================================
// DYNAMIC SPACE CARD (for smart suggestions)
// =============================================================================

type DynamicSpaceCardProps = {
  suggestion: DynamicSpaceSuggestion;
  onSelect: () => void;
};

/**
 * Card for dynamically suggested spaces (sequential or sub-spaces).
 * Looks the same as regular SpaceCard to blend in naturally.
 */
function DynamicSpaceCard({ suggestion, onSelect }: DynamicSpaceCardProps) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={classNames(
        'group relative flex flex-col items-center justify-center rounded-xl border-2 p-3 transition-all duration-200',
        'min-h-[100px] w-full',
        'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm'
      )}
      aria-label={`Add ${suggestion.displayName}`}
      tabIndex={0}
    >
      {/* Icon */}
      <div
        className={classNames(
          'mb-2 flex h-8 w-8 items-center justify-center rounded-full transition-colors',
          'text-grey-50 group-hover:text-grey-70'
        )}
      >
        <FontAwesomeIcon icon={suggestion.icon} className='h-5 w-5' />
      </div>

      {/* Label - show displayName (e.g., "Ensuite" not "Bedroom 1/Ensuite") */}
      <span
        className={classNames(
          'text-center text-sm font-medium',
          'text-grey-70'
        )}
      >
        {suggestion.displayName}
      </span>
    </button>
  );
}

// =============================================================================
// CONSTANTS FOR LAYOUT CALCULATIONS
// =============================================================================

// Card height (min-h-[100px]) + gap (12px = gap-3) for row calculation
// Aligned with AssetCardSelector for consistent height limits
const CARD_HEIGHT = 100;
const GAP_SIZE = 12;

// Estimate of other composer elements height (buttons, summary, progress bar, padding)
// This is subtracted from the available composer height to calculate card grid space
const COMPOSER_FIXED_ELEMENTS_HEIGHT = 180;

// Grid columns by breakpoint: base=3, sm=4, md=5
const getColumnsForBreakpoint = (isMobile: boolean, isMd: boolean): number => {
  if (isMd) return 5;
  if (!isMobile) return 4;
  return 3;
};

/**
 * Calculate how many rows of cards can fit in the available height.
 * Returns the number of rows that fit, minimum 1.
 */
const calculateMaxRows = (availableHeight: number): number => {
  if (availableHeight <= 0) return 1;

  // Each row takes CARD_HEIGHT + GAP_SIZE (except the last row which doesn't need gap after)
  // So for N rows: N * CARD_HEIGHT + (N - 1) * GAP_SIZE <= availableHeight
  // Solving: N * (CARD_HEIGHT + GAP_SIZE) - GAP_SIZE <= availableHeight
  // N <= (availableHeight + GAP_SIZE) / (CARD_HEIGHT + GAP_SIZE)
  const maxRows = Math.floor(
    (availableHeight + GAP_SIZE) / (CARD_HEIGHT + GAP_SIZE)
  );
  return Math.max(1, maxRows);
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export type SpaceCardSelectorProps = {
  industry: string;
  placeType: string | null;
  selectedSpaces: string[];
  customSpaces: string[];
  onSelectionChange: (spaces: string[]) => void;
  onCustomSpacesChange: (customSpaces: string[]) => void;
  /** Optional: available height for the card grid (in px). If not provided, uses 60vh calculation. */
  availableHeight?: number;
  /** Optional: ref to the container that expanded cards should stay within */
  expandContainerRef?: React.RefObject<HTMLElement>;
};

export function SpaceCardSelector({
  industry,
  placeType,
  selectedSpaces,
  customSpaces,
  onSelectionChange,
  onCustomSpacesChange,
  availableHeight: externalAvailableHeight,
  expandContainerRef,
}: SpaceCardSelectorProps) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [calculatedMaxRows, setCalculatedMaxRows] = useState<number>(3);
  const [expandBounds, setExpandBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Calculate expand bounds when custom input is shown
  useEffect(() => {
    if (!showCustomInput) {
      setExpandBounds(null);
      return;
    }

    const updateBounds = () => {
      const container = expandContainerRef?.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        setExpandBounds({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      } else {
        // Fallback to viewport
        setExpandBounds({
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    // Initial calculation
    updateBounds();

    const container = expandContainerRef?.current;
    if (container) {
      // Use ResizeObserver for container element
      const resizeObserver = new ResizeObserver(updateBounds);
      resizeObserver.observe(container);

      // Cleanup function
      return () => {
        resizeObserver.disconnect();
      };
    } else {
      // Use window resize event for viewport fallback
      const handleResize = () => updateBounds();
      window.addEventListener('resize', handleResize);

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showCustomInput, expandContainerRef]);

  const { isMobile, isTablet } = useBreakpoint();

  const suggestions = getSpaceSuggestionsForPlaceType(industry, placeType);
  const allSpaceNames = [...suggestions.map((s) => s.name), ...customSpaces];

  // Compute dynamic suggestions based on selected spaces
  // NOTE: This useMemo recomputes when selectedSpaces, customSpaces, or suggestions change.
  // It provides contextual space suggestions like "Ensuite" when "Bedroom 1" is selected,
  // or "Bedroom 3" when "Bedroom 2" is selected.
  const dynamicSuggestions = useMemo(
    () => getDynamicSpaceSuggestions(selectedSpaces, customSpaces, suggestions),
    [selectedSpaces, customSpaces, suggestions]
  );

  // Calculate total items (+1 for "Add Custom" button + dynamic suggestions)
  const totalItems =
    suggestions.length + customSpaces.length + dynamicSuggestions.length + 1;

  // Calculate columns based on breakpoint
  const columns = getColumnsForBreakpoint(isMobile, isTablet);

  // Effect: Calculate max rows based on breakpoint
  // NOTE: Mobile max 3 rows, tablet max 4 rows to prevent overflow in conversational onboarding
  useEffect(() => {
    const calculateAvailableRows = () => {
      // Apply device-specific caps
      if (isMobile) {
        setCalculatedMaxRows(3); // Mobile: max 3 rows
      } else if (isTablet) {
        setCalculatedMaxRows(4); // Tablet: max 4 rows
      } else {
        // Desktop: calculate based on available height
        const availableHeight =
          externalAvailableHeight ??
          window.innerHeight * 0.6 - COMPOSER_FIXED_ELEMENTS_HEIGHT;
        const maxRows = calculateMaxRows(availableHeight);
        setCalculatedMaxRows(maxRows);
      }
    };

    calculateAvailableRows();
    window.addEventListener('resize', calculateAvailableRows);
    return () => window.removeEventListener('resize', calculateAvailableRows);
  }, [externalAvailableHeight, isMobile, isTablet]);

  // Calculate actual rows needed for current items
  const rowsNeeded = Math.ceil(totalItems / columns);
  const needsHorizontalScroll = rowsNeeded > calculatedMaxRows;

  // For horizontal scroll mode, use calculated max rows
  const displayRows = needsHorizontalScroll ? calculatedMaxRows : rowsNeeded;

  // Update scroll button visibility
  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Effect: Update scroll buttons on resize and scroll
  // NOTE: This effect is needed because the scroll container dimensions change
  // when the window resizes or when items are added/removed.
  useEffect(() => {
    if (!needsHorizontalScroll) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();
    container.addEventListener('scroll', updateScrollButtons, {
      passive: true,
    });
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [needsHorizontalScroll, updateScrollButtons, totalItems]);

  const handleScrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  const collectDependentSubSpaces = useCallback(
    (parentName: string): string[] => {
      const directChildren = customSpaces.filter(
        (customName) => customName.split('/')[0] === parentName
      );
      return directChildren.flatMap((childName) => [
        childName,
        ...collectDependentSubSpaces(childName),
      ]);
    },
    [customSpaces]
  );

  const toggleSpace = (spaceName: string) => {
    if (selectedSpaces.includes(spaceName)) {
      const dependentSubSpaces = collectDependentSubSpaces(spaceName);
      const sequentialSpacesToRemove = customSpaces.filter((cs) => {
        if (selectedSpaces.includes(cs)) return false;

        const parsed = parseSpaceName(spaceName);
        const parsedChild = parseSpaceName(cs);
        if (
          parsed.baseName === parsedChild.baseName &&
          parsed.suffixType === parsedChild.suffixType &&
          parsed.suffix !== null &&
          parsedChild.suffix !== null
        ) {
          if (parsed.suffixType === 'number') {
            return (parsedChild.suffix as number) > (parsed.suffix as number);
          }
          if (parsed.suffixType === 'letter') {
            return (
              (parsedChild.suffix as string).charCodeAt(0) >
              (parsed.suffix as string).charCodeAt(0)
            );
          }
        }
        return false;
      });

      const customSpacesToRemove = Array.from(
        new Set([...dependentSubSpaces, ...sequentialSpacesToRemove])
      );

      const updatedSelectedSpaces = selectedSpaces.filter(
        (selectedSpace) =>
          selectedSpace !== spaceName &&
          !dependentSubSpaces.includes(selectedSpace)
      );

      const updatedCustomSpaces =
        customSpacesToRemove.length > 0
          ? customSpaces.filter((cs) => !customSpacesToRemove.includes(cs))
          : customSpaces;

      onSelectionChange(updatedSelectedSpaces);

      if (customSpacesToRemove.length > 0) {
        onCustomSpacesChange(updatedCustomSpaces);
      }
    } else {
      onSelectionChange([...selectedSpaces, spaceName]);
    }
  };

  const handleAddCustom = (name: string) => {
    onCustomSpacesChange([...customSpaces, name]);
    onSelectionChange([...selectedSpaces, name]);
    setShowCustomInput(false);
  };

  const handleRemoveCustom = (name: string) => {
    onCustomSpacesChange(customSpaces.filter((s) => s !== name));
    onSelectionChange(selectedSpaces.filter((s) => s !== name));
  };

  // Handle selecting a dynamic suggestion
  // - For sequential suggestions (e.g., "Bedroom 3"), add as custom space + select
  // - For sub-spaces (e.g., "Bedroom 1/Ensuite"), add the full name as custom space + select
  const handleSelectDynamicSuggestion = (
    suggestion: DynamicSpaceSuggestion
  ) => {
    const spaceName = suggestion.name;
    // Add to custom spaces (since it's not in base suggestions)
    onCustomSpacesChange([...customSpaces, spaceName]);
    // Also select it
    onSelectionChange([...selectedSpaces, spaceName]);
  };

  // Calculate container height for horizontal scroll mode
  const horizontalScrollHeight =
    displayRows * CARD_HEIGHT + (displayRows - 1) * GAP_SIZE;

  // Calculate the number of columns needed for horizontal scroll mode
  // This ensures items flow left-to-right, row by row (not column by column)
  const horizontalScrollColumns = Math.ceil(totalItems / displayRows);

  // Group dynamic suggestions by their trigger space for interleaved rendering
  // This allows us to render "Ensuite" right after "Bedroom 1" when selected
  const suggestionsByTrigger = useMemo(() => {
    const map = new Map<string, DynamicSpaceSuggestion[]>();
    for (const suggestion of dynamicSuggestions) {
      const existing = map.get(suggestion.triggerSpace) ?? [];
      existing.push(suggestion);
      map.set(suggestion.triggerSpace, existing);
    }
    return map;
  }, [dynamicSuggestions]);

  // Find the parent space for a custom space name
  // Returns the parent ONLY for true sub-spaces (e.g., "Bedroom 1" for "Bedroom 1/Ensuite")
  // Sequential spaces like "Bedroom 3" are NOT children of "Bedroom 2" - they're peers
  const findParentSpace = useCallback(
    (customName: string): string | null => {
      // Only sub-spaces (containing "/") have true parent-child relationships
      // e.g., "Bedroom 1/Ensuite" → parent is "Bedroom 1"
      if (customName.includes('/')) {
        const parentName = customName.split('/')[0];
        // Check if parent exists in suggestions or other custom spaces
        if (
          suggestions.some((s) => s.name === parentName) ||
          customSpaces.includes(parentName)
        ) {
          return parentName;
        }
      }

      // Sequential spaces (e.g., "Bedroom 3") are peers, not children
      // They will be rendered as standalone spaces near their base type
      return null;
    },
    [suggestions, customSpaces]
  );

  // Find the anchor space for rendering order (where a custom space should appear)
  // For sequential spaces (e.g., "Bedroom 3"), find the HIGHEST base suggestion of the same type
  // This groups all bedrooms together: Bedroom 2 (base) → Bedroom 3 → Bedroom 4 → Bedroom 5...
  const findAnchorSpace = useCallback(
    (customName: string): string | null => {
      const parsed = parseSpaceName(customName);

      // Only sequential spaces need anchoring
      if (parsed.suffix === null || parsed.suffixType === null) return null;

      // Find the highest numbered/lettered space of the same base in suggestions
      let highestAnchor: string | null = null;
      let highestValue = 0;

      for (const suggestion of suggestions) {
        const suggestionParsed = parseSpaceName(suggestion.name);
        if (
          suggestionParsed.baseName === parsed.baseName &&
          suggestionParsed.suffixType === parsed.suffixType &&
          suggestionParsed.suffix !== null
        ) {
          const value =
            parsed.suffixType === 'number'
              ? (suggestionParsed.suffix as number)
              : (suggestionParsed.suffix as string).charCodeAt(0);
          if (value > highestValue) {
            highestValue = value;
            highestAnchor = suggestion.name;
          }
        }
      }

      return highestAnchor;
    },
    [suggestions]
  );

  // Separate custom spaces into:
  // - "subSpaces" that are true children (contain "/") → render under parent
  // - "sequentialSpaces" that are peers → render after highest base suggestion of same type
  // - "standalone" ones that have no relationship → render in standalone section
  const { subSpaces, sequentialSpaces, standaloneCustomSpaces } =
    useMemo(() => {
      const subs = new Map<string, string[]>(); // parent → sub-space children
      const sequential = new Map<string, string[]>(); // anchor → sequential peers (sorted)
      const standalone: string[] = [];

      for (const name of customSpaces) {
        const parent = findParentSpace(name);

        if (parent) {
          // True sub-space (contains "/")
          const existing = subs.get(parent) ?? [];
          existing.push(name);
          subs.set(parent, existing);
        } else {
          // Check if it's a sequential space that chains after a previous space
          const anchor = findAnchorSpace(name);
          if (anchor) {
            const existing = sequential.get(anchor) ?? [];
            existing.push(name);
            sequential.set(anchor, existing);
          } else {
            standalone.push(name);
          }
        }
      }

      // Sort sequential peers by suffix to ensure correct order (3, 4, 5, not 5, 3, 4)
      for (const [, peers] of sequential.entries()) {
        peers.sort((a, b) => {
          const parsedA = parseSpaceName(a);
          const parsedB = parseSpaceName(b);
          if (
            parsedA.suffixType === 'number' &&
            parsedB.suffixType === 'number'
          ) {
            return (parsedA.suffix as number) - (parsedB.suffix as number);
          }
          if (
            parsedA.suffixType === 'letter' &&
            parsedB.suffixType === 'letter'
          ) {
            return (parsedA.suffix as string).localeCompare(
              parsedB.suffix as string
            );
          }
          return 0;
        });
      }

      return {
        subSpaces: subs,
        sequentialSpaces: sequential,
        standaloneCustomSpaces: standalone,
      };
    }, [customSpaces, findParentSpace, findAnchorSpace]);

  // Helper: Render a single custom space with its sub-spaces and dynamic suggestions
  // Order: Item → Sub-spaces (selected ensuites) → Dynamic suggestions
  // Sub-spaces come FIRST so selected ensuites stay next to their parent bedroom
  // Does NOT render sequential peers - those are handled at the top level
  const renderSingleCustomSpace = (name: string): React.ReactNode[] => {
    const childSubSpaces = subSpaces.get(name) ?? [];
    const dynamicSuggestions = suggestionsByTrigger.get(name) ?? [];

    return [
      // 1. The item itself
      <CustomSpaceChip
        key={`custom-${name}`}
        name={name}
        displayName={getDisplayName(name)}
        icon={inferSpaceIcon(name, suggestions)}
        selected={selectedSpaces.includes(name)}
        onToggle={() => toggleSpace(name)}
        onRemove={() => handleRemoveCustom(name)}
      />,
      // 2. Sub-spaces FIRST - selected ensuites stay next to their parent bedroom
      ...childSubSpaces.flatMap((subSpaceName): React.ReactNode[] =>
        renderSingleCustomSpace(subSpaceName)
      ),
      // 3. Dynamic suggestions (e.g., Bedroom 5 suggestion) come AFTER sub-spaces
      ...dynamicSuggestions.map((suggestion) => (
        <DynamicSpaceCard
          key={`dynamic-${suggestion.name}`}
          suggestion={suggestion}
          onSelect={() => handleSelectDynamicSuggestion(suggestion)}
        />
      )),
    ];
  };

  // Render all cards - SIMPLIFIED APPROACH for stable ordering
  // Order: Add button → All base suggestions (with their related customs/suggestions inline)
  const renderCards = () => {
    // Track which custom spaces have been rendered to avoid duplicates
    const renderedCustoms = new Set<string>();
    const cards: React.ReactNode[] = [];

    // Add custom button - expands to show form
    cards.push(
      <ExpandingCustomSpaceInput
        key='custom-input'
        isExpanded={showCustomInput}
        onExpand={() => setShowCustomInput(true)}
        onAdd={handleAddCustom}
        onCancel={() => setShowCustomInput(false)}
        existingNames={allSpaceNames}
        expandBounds={expandBounds ?? undefined}
      />
    );

    // Render base suggestions with related customs and dynamic suggestions inline
    suggestions.forEach((space) => {
      // 1. The base suggestion card
      cards.push(
        <SpaceCard
          key={space.name}
          space={space}
          selected={selectedSpaces.includes(space.name)}
          onToggle={() => toggleSpace(space.name)}
        />
      );

      // 2. Sub-spaces (e.g., "Bedroom 1/Ensuite") - render immediately after parent
      const childSubSpaces = subSpaces.get(space.name) ?? [];
      childSubSpaces.forEach((subSpaceName) => {
        cards.push(...renderSingleCustomSpace(subSpaceName));
        renderedCustoms.add(subSpaceName);
      });

      // 3. Dynamic suggestions - render after sub-spaces
      const dynamicSuggestions = suggestionsByTrigger.get(space.name) ?? [];
      dynamicSuggestions.forEach((suggestion) => {
        cards.push(
          <DynamicSpaceCard
            key={`dynamic-${suggestion.name}`}
            suggestion={suggestion}
            onSelect={() => handleSelectDynamicSuggestion(suggestion)}
          />
        );
      });

      // 4. Sequential peers (e.g., "Bedroom 3" after "Bedroom 2")
      const sequentialPeers = sequentialSpaces.get(space.name) ?? [];
      sequentialPeers.forEach((peerName) => {
        cards.push(...renderSingleCustomSpace(peerName));
        renderedCustoms.add(peerName);
      });
    });

    // Standalone custom spaces (no parent relationship) - render at end
    standaloneCustomSpaces
      .filter((name) => !renderedCustoms.has(name))
      .forEach((name) => {
        cards.push(...renderSingleCustomSpace(name));
      });

    return cards;
  };

  // Render cards for display - no interleaving needed since we use grid-auto-flow: row
  // Items flow naturally left-to-right, row by row
  const renderCardsForMode = () => {
    return renderCards();
  };

  return (
    <div ref={containerRef} className='w-full'>
      {needsHorizontalScroll ? (
        /* Horizontal scroll mode - when content exceeds calculated max rows */
        <div className='relative'>
          {/* Left scroll button */}
          {canScrollLeft && (
            <button
              type='button'
              onClick={handleScrollLeft}
              className='absolute -left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110'
              aria-label='Scroll left'
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                className='text-grey-60 h-4 w-4'
              />
            </button>
          )}

          {/* Scrollable container with explicit columns - items flow left-to-right, row by row */}
          <div
            ref={scrollContainerRef}
            className='scrollbar-hide grid gap-3 overflow-x-auto scroll-smooth px-1'
            style={{
              height: horizontalScrollHeight,
              gridTemplateColumns: `repeat(${horizontalScrollColumns}, minmax(120px, 1fr))`,
              gridTemplateRows: `repeat(${displayRows}, ${CARD_HEIGHT}px)`,
            }}
          >
            {renderCardsForMode()}
          </div>

          {/* Right scroll button */}
          {canScrollRight && (
            <button
              type='button'
              onClick={handleScrollRight}
              className='absolute -right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110'
              aria-label='Scroll right'
            >
              <FontAwesomeIcon
                icon={faChevronRight}
                className='text-grey-60 h-4 w-4'
              />
            </button>
          )}
        </div>
      ) : (
        /* Standard grid mode - when content fits within calculated max rows */
        <div className='grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5'>
          {renderCardsForMode()}
        </div>
      )}
    </div>
  );
}
